import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ChatbotWidget from '../components/ChatbotWidget';
import DonationModal from '../components/DonationModal';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Import views
import { 
  DashboardView, 
  CompanyProfileView, 
  CampaignsView, 
  BudgetView, 
  TeamView, 
  ImpactWebsiteView, 
  MarketingView,
  ReportsView,
  VolunteerView,
  GamificationView,
  HRIntegrationsView,
  MatchingGiftView,
  CharityDiscoverView
} from '../components/dashboard/views';
import DonationsView from '../components/dashboard/views/DonationsView';

// Import modals
import { 
  CreateCampaignModal, 
  EditCampaignModal, 
  BudgetSettingsModal, 
  InviteUserModal 
} from '../components/dashboard/modals';

// Import constants
import { COLORS, INDUSTRIES } from '../constants/dashboard';

// Import onboarding
import OnboardingWizard from '../components/OnboardingWizard';
import SubscriptionView from '../components/dashboard/views/SubscriptionView';
import HelpSupportView from '../components/dashboard/views/HelpSupportView';
import CharityNotesView from '../components/dashboard/views/CharityNotesView';
import PayrollGivingView from '../components/dashboard/views/PayrollGivingView';

// Icons for header navigation
import { 
  Building2, BarChart3, Target, Wallet, Globe, Megaphone, Users, 
  Bell, ChevronRight, FileText, Clock, Trophy, Link2, ChevronDown,
  User, Lock, Settings, LogOut, HelpCircle, Gift, CreditCard, Palette, Compass,
  Receipt, MessageSquareQuote, Banknote
} from 'lucide-react';

// Leaflet icon fix
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const UnifiedDashboard = () => {
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Navigation state
  const [activeView, setActiveView] = useState(() => searchParams.get('view') || 'dashboard');

  // Ari deep-links here with ?view=<id> from its "Take me there" button
  useEffect(() => {
    const view = searchParams.get('view');
    if (view) setActiveView(view);
  }, [searchParams]);

  // tell Ari which screen the user is looking at, so it can answer in context
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('altrunic:view-change', { detail: { view: activeView } }));
  }, [activeView]);

  // Listen for custom navigation events (from child components)
  useEffect(() => {
    const handleNavigateTo = (e) => setActiveView(e.detail);
    window.addEventListener('navigate-to', handleNavigateTo);
    return () => window.removeEventListener('navigate-to', handleNavigateTo);
  }, []);
  
  // Super Admin managing a business
  const managingBusinessId = searchParams.get('manage');
  const [managingBusiness, setManagingBusiness] = useState(null);
  
  // Core states
  const [user, setUser] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [businessProfile, setBusinessProfile] = useState(null);
  const [showOnboarding, setShowOnboarding] = useState(false);
  
  // User dropdown & modals
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  
  // Company Profile form state
  const [profileForm, setProfileForm] = useState({
    name: '', industry: '', employee_count: '', website: '', email: '', phone: '',
    address: '', city: '', state: '', zip_code: '', logo_url: '', mission_statement: '',
    csr_goals: [], target_causes: [], geographic_focus: [], csr_budget: '', esg_priorities: [],
    country: '', address_street: '', address_city: '', address_state: '', address_zip: '', phone_number: ''
  });
  const [savingProfile, setSavingProfile] = useState(false);
  
  // Campaign states
  const [campaigns, setCampaigns] = useState([]);
  const [campaignTab, setCampaignTab] = useState('my-campaigns');
  
  // Charity discovery states
  const [charities, setCharities] = useState([]);
  const [charitiesLoading, setCharitiesLoading] = useState(false);
  const [selectedCharity, setSelectedCharity] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [discoveryMode, setDiscoveryMode] = useState('browse');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [locationMode, setLocationMode] = useState('nationwide');
  const [zipCode, setZipCode] = useState('');
  const [selectedState, setSelectedState] = useState('');
  const [hoveredCharity, setHoveredCharity] = useState(null);
  
  // Portfolio Builder sliders (total should equal 100)
  const [portfolioWeights, setPortfolioWeights] = useState({
    geographic: 33,
    csr: 34,
    esg: 33
  });
  const [portfolioCoverage, setPortfolioCoverage] = useState(null);
  
  // Campaign creation
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [campaignForm, setCampaignForm] = useState({ 
    title: '', goal_amount: 10000, description: '',
    employer_match_enabled: false, employer_match_ratio: 1, employer_match_cap: 5000
  });
  const [creating, setCreating] = useState(false);

  // Edit Campaign Modal
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);
  
  // Budget Management - Default to 0 for new companies
  const [budget, setBudget] = useState({
    annual_budget: 0, used: 0, departments: [],
    match_cap_per_employee: 0, match_ratio: 1, auto_approve_limit: 0
  });
  const [showBudgetModal, setShowBudgetModal] = useState(false);
  
  // Matching settings from company profile
  const [matchingSettings, setMatchingSettings] = useState({
    default_match_ratio: 1,
    annual_match_budget_per_employee: 1000,
    matching_enabled: true
  });
  
  // User Management
  const [users, setUsers] = useState([]);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: '', role: 'employee' });

  // Donation modal
  const [showDonationModal, setShowDonationModal] = useState(false);
  const [donationCharity, setDonationCharity] = useState(null);

  // Calculate budget usage
  const budgetUsed = campaigns.reduce((sum, c) => sum + (c.raised_amount || 0) * (c.employer_match_enabled ? c.employer_match_ratio : 0), 0);
  const budgetPercentage = budget.annual_budget > 0 ? (budgetUsed / budget.annual_budget) * 100 : 0;

  useEffect(() => {
    fetchInitialData();
  }, [managingBusinessId]);

  const fetchInitialData = async () => {
    try {
      const [userRes, analyticsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/auth/me`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/analytics/overview`, { withCredentials: true })
      ]);

      setUser(userRes.data);
      
      if (userRes.data.role === 'super_admin' && managingBusinessId) {
        try {
          const [businessRes, campaignsRes] = await Promise.all([
            axios.get(`${BACKEND_URL}/api/businesses/${managingBusinessId}`, { withCredentials: true }),
            axios.get(`${BACKEND_URL}/api/campaigns?business_id=${managingBusinessId}`, { withCredentials: true })
          ]);
          setManagingBusiness(businessRes.data);
          setBusinessProfile(businessRes.data);
          setCampaigns(campaignsRes.data || []);
          setStats({
            ...analyticsRes.data,
            campaigns: campaignsRes.data || [],
            total_campaigns: campaignsRes.data?.length || 0,
            total_raised: campaignsRes.data?.reduce((sum, c) => sum + (c.raised_amount || 0), 0) || 0
          });
        } catch (err) {
          console.error('Error fetching managed business:', err);
        }
      } else {
        setStats(analyticsRes.data);
        setCampaigns(analyticsRes.data.campaigns || []);
        
        if (userRes.data.business_id) {
          try {
            const businessRes = await axios.get(
              `${BACKEND_URL}/api/businesses/${userRes.data.business_id}`,
              { withCredentials: true }
            );
            setBusinessProfile(businessRes.data);
            // Removed auto-popup of onboarding modal on first login
            // Users can complete profile via the Profile tab instead
            if (businessRes.data.budget) {
              setBudget(businessRes.data.budget);
            }
            // Fetch matching settings for campaign form defaults
            try {
              const matchRes = await axios.get(`${BACKEND_URL}/api/matching/settings`, { withCredentials: true });
              setMatchingSettings(matchRes.data);
            } catch (err) { /* matching settings are optional */ }
            const industryLower = (businessRes.data.industry || '').toLowerCase();
            const matchedIndustry = INDUSTRIES.find(ind => ind.value === industryLower || ind.label.toLowerCase().includes(industryLower))?.value || industryLower;
            
            setProfileForm({
              name: businessRes.data.name || '',
              industry: matchedIndustry,
              employee_count: businessRes.data.employee_count || '',
              website: businessRes.data.website || '',
              email: businessRes.data.email || '',
              phone: businessRes.data.phone || '',
              address: businessRes.data.address || '',
              city: businessRes.data.city || '',
              state: businessRes.data.state || '',
              zip_code: businessRes.data.zip_code || '',
              logo_url: businessRes.data.logo_url || '',
              mission_statement: businessRes.data.mission_statement || '',
              address_street: businessRes.data.address_street || '',
              address_city: businessRes.data.address_city || '',
              address_state: businessRes.data.address_state || '',
              address_zip: businessRes.data.address_zip || '',
              phone_number: businessRes.data.phone_number || '',
              country: businessRes.data.country || '',
              csr_goals: businessRes.data.csr_goals || [],
              target_causes: (businessRes.data.target_causes || []).filter(c => c && c.trim() !== ''),
              geographic_focus: (businessRes.data.geographic_focus || []).filter(g => g && g.trim() !== ''),
              csr_budget: businessRes.data.csr_budget || '',
              esg_priorities: businessRes.data.esg_priorities || []
            });
          } catch (err) {
            // Business not found - user can create via Profile tab
            console.log('Business profile not found');
          }
        }
      }
      
      try {
        const usersRes = await axios.get(`${BACKEND_URL}/api/users`, { withCredentials: true });
        setUsers(usersRes.data.users || []);
      } catch (err) {
        console.log('Users fetch skipped');
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      navigate('/');
    }
  };

  const saveCompanyProfile = async () => {
    setSavingProfile(true);
    try {
      await axios.put(
        `${BACKEND_URL}/api/businesses/${businessProfile?.business_id || user?.business_id}`,
        { ...profileForm, onboarding_completed: true },
        { withCredentials: true }
      );
      const businessRes = await axios.get(
        `${BACKEND_URL}/api/businesses/${businessProfile?.business_id || user?.business_id}`,
        { withCredentials: true }
      );
      setBusinessProfile(businessRes.data);
      alert('Profile saved successfully!');
    } catch (err) {
      console.error('Error saving profile:', err);
      alert('Failed to save profile');
    } finally {
      setSavingProfile(false);
    }
  };

  const toggleCampaignStatus = async (campaign, targetStatus) => {
    try {
      const newStatus = targetStatus || (campaign.status === 'active' ? 'paused' : 'active');
      await axios.put(
        `${BACKEND_URL}/api/campaigns/${campaign.campaign_id}`,
        { status: newStatus },
        { withCredentials: true }
      );
      const analyticsRes = await axios.get(`${BACKEND_URL}/api/analytics/overview`, { withCredentials: true });
      setCampaigns(analyticsRes.data.campaigns || []);
    } catch (err) {
      console.error('Error updating campaign:', err);
    }
  };

  const saveCampaignEdit = async () => {
    setSavingEdit(true);
    try {
      await axios.put(
        `${BACKEND_URL}/api/campaigns/${editingCampaign.campaign_id}`,
        editForm,
        { withCredentials: true }
      );
      const analyticsRes = await axios.get(`${BACKEND_URL}/api/analytics/overview`, { withCredentials: true });
      setCampaigns(analyticsRes.data.campaigns || []);
      setShowEditModal(false);
      setEditingCampaign(null);
    } catch (err) {
      console.error('Error saving campaign:', err);
      alert('Failed to save campaign');
    } finally {
      setSavingEdit(false);
    }
  };

  const openEditCampaign = (campaign) => {
    setEditingCampaign(campaign);
    setEditForm({
      title: campaign.title,
      description: campaign.description || '',
      goal_amount: campaign.goal_amount,
      employer_match_enabled: campaign.employer_match_enabled || false,
      employer_match_ratio: campaign.employer_match_ratio || 1,
      employer_match_cap_per_employee: campaign.employer_match_cap_per_employee || 500
    });
    setShowEditModal(true);
  };

  const fetchAIRecommendations = async () => {
    if (!businessProfile?.onboarding_completed) {
      alert('Please complete your Company Profile in the Profile tab to unlock AI recommendations');
      setActiveView('settings-company');
      return;
    }
    setCharitiesLoading(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/ai/recommendations`, { withCredentials: true });
      setCharities(response.data.organizations || []);
    } catch (err) {
      console.error('Error fetching AI recommendations:', err);
      alert('Failed to get AI recommendations. Please try Browse instead.');
    } finally {
      setCharitiesLoading(false);
    }
  };

  const fetchPortfolioRecommendations = async (weights = portfolioWeights, filters = {}) => {
    setCharitiesLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('geo_weight', weights.geographic);
      params.append('csr_weight', weights.csr);
      params.append('esg_weight', weights.esg);
      
      // Add keyword/category/location filters (keyword takes precedence)
      if (filters.q || searchQuery) params.append('q', filters.q || searchQuery);
      if (filters.category || selectedCategory) params.append('category', filters.category || selectedCategory);
      if (filters.state || (locationMode === 'state' && selectedState)) {
        params.append('state', filters.state || selectedState);
      }
      if (filters.zip_code || (locationMode === 'local' && zipCode)) {
        params.append('zip_code', filters.zip_code || zipCode);
      }
      if (filters.radius) {
        params.append('radius', filters.radius);
      }
      
      const response = await axios.get(
        `${BACKEND_URL}/api/ai/portfolio-recommendations?${params.toString()}`,
        { withCredentials: true }
      );
      
      setCharities(response.data.organizations || []);
      setPortfolioCoverage(response.data.coverage || null);
    } catch (err) {
      console.error('Error fetching portfolio recommendations:', err);
      alert('Failed to get recommendations. Please complete your profile.');
    } finally {
      setCharitiesLoading(false);
    }
  };

  // Handle slider changes (keep total at 100)
  const handleWeightChange = (pillar, newValue) => {
    // Ensure newValue is a number
    const value = parseInt(newValue, 10);
    const otherPillars = Object.keys(portfolioWeights).filter(p => p !== pillar);
    const newOtherTotal = 100 - value;
    
    // If new value is 100 or more, set others to 0
    if (value >= 100) {
      const newWeights = { [pillar]: 100 };
      otherPillars.forEach(p => {
        newWeights[p] = 0;
      });
      setPortfolioWeights(newWeights);
      return;
    }
    
    // If new value is 0 or less, distribute 100 among others
    if (value <= 0) {
      const newWeights = { [pillar]: 0 };
      const perOther = Math.floor(100 / otherPillars.length);
      otherPillars.forEach((p, i) => {
        newWeights[p] = perOther;
      });
      // Fix rounding
      const total = Object.values(newWeights).reduce((a, b) => a + b, 0);
      if (total !== 100) {
        newWeights[otherPillars[0]] += (100 - total);
      }
      setPortfolioWeights(newWeights);
      return;
    }
    
    const currentOtherTotal = otherPillars.reduce((sum, p) => sum + portfolioWeights[p], 0);
    
    if (currentOtherTotal === 0) {
      // Distribute equally among others
      const newWeights = { [pillar]: value };
      const perOther = Math.floor(newOtherTotal / otherPillars.length);
      otherPillars.forEach(p => {
        newWeights[p] = perOther;
      });
      // Fix rounding
      const total = Object.values(newWeights).reduce((a, b) => a + b, 0);
      if (total !== 100) {
        newWeights[otherPillars[0]] += (100 - total);
      }
      setPortfolioWeights(newWeights);
    } else {
      // Proportionally adjust others
      const ratio = newOtherTotal / currentOtherTotal;
      const newWeights = { [pillar]: value };
      otherPillars.forEach(p => {
        newWeights[p] = Math.max(0, Math.round(portfolioWeights[p] * ratio));
      });
      // Fix rounding errors
      const total = Object.values(newWeights).reduce((a, b) => a + b, 0);
      if (total !== 100) {
        newWeights[otherPillars[0]] += (100 - total);
      }
      setPortfolioWeights(newWeights);
    }
  };

  // Preset weight configurations
  const applyPreset = (preset) => {
    const presets = {
      balanced: { geographic: 33, csr: 34, esg: 33 },
      esgHeavy: { geographic: 20, csr: 20, esg: 60 },
      causeFirst: { geographic: 20, csr: 60, esg: 20 },
      localImpact: { geographic: 60, csr: 20, esg: 20 }
    };
    setPortfolioWeights(presets[preset] || presets.balanced);
  };

  const handleBrowseSearch = async () => {
    setCharitiesLoading(true);
    try {
      const params = new URLSearchParams();
      let query = searchQuery || '';
      if (selectedCategory) {
        query = query ? `${query} ${selectedCategory}` : selectedCategory;
      }
      if (query) params.append('q', query);
      if (locationMode === 'local' && zipCode) params.append('zip_code', zipCode);
      if (locationMode === 'state' && selectedState) params.append('state', selectedState);
      params.append('limit', '24');
      
      const response = await axios.get(`${BACKEND_URL}/api/charities/search?${params.toString()}`, { withCredentials: true });
      setCharities(response.data.organizations || []);
    } catch (err) {
      console.error('Search error:', err);
    } finally {
      setCharitiesLoading(false);
    }
  };

  const handleCharitySelect = (charity) => {
    setSelectedCharity(charity);
    setShowCreateForm(true);
    setCampaignForm({
      title: `Support ${charity.name}`,
      goal_amount: 10000,
      description: `Partner with ${charity.name} to make a positive impact.`,
      employer_match_enabled: matchingSettings.matching_enabled || false,
      employer_match_ratio: matchingSettings.default_match_ratio || 1,
      employer_match_cap: matchingSettings.annual_match_budget_per_employee || 1000
    });
  };

  const handleDonate = (charity) => {
    setDonationCharity(charity);
    setShowDonationModal(true);
  };

  const handleDonationSuccess = async (donationData) => {
    toast.success('Donation successful!', {
      description: `$${donationData.amount} donated to ${donationData.charity_name}`,
    });
    // Refresh campaign data to reflect new donation amounts
    try {
      const analyticsRes = await axios.get(`${BACKEND_URL}/api/analytics/overview`, { withCredentials: true });
      setCampaigns(analyticsRes.data.campaigns || []);
      setStats(analyticsRes.data);
    } catch (err) {
      console.error('Error refreshing after donation:', err);
    }
  };

  const handleCreateCampaign = async () => {
    if (!selectedCharity) return;
    setCreating(true);
    try {
      const impactMetrics = {
        'Education': { metric: 'students supported', rate: 0.1 },
        'Health Care': { metric: 'patients helped', rate: 0.05 },
        'Human Services': { metric: 'people assisted', rate: 0.08 },
        'Environment': { metric: 'trees planted', rate: 0.5 },
        'Animal-Related': { metric: 'animals saved', rate: 0.15 },
        'default': { metric: 'lives impacted', rate: 0.1 }
      };
      const impact = impactMetrics[selectedCharity.category] || impactMetrics.default;
      
      await axios.post(`${BACKEND_URL}/api/campaigns`, {
        charity_name: selectedCharity.name,
        charity_ein: selectedCharity.ein,
        title: campaignForm.title,
        description: campaignForm.description,
        goal_amount: parseFloat(campaignForm.goal_amount),
        impact_metric: impact.metric,
        impact_conversion_rate: impact.rate,
        employer_match_enabled: campaignForm.employer_match_enabled,
        employer_match_ratio: campaignForm.employer_match_enabled ? parseFloat(campaignForm.employer_match_ratio) : 0,
        employer_match_cap_per_employee: campaignForm.employer_match_enabled ? parseFloat(campaignForm.employer_match_cap) : 0,
        esg_score: selectedCharity.esg_score,
        marketing_score: selectedCharity.marketing_score,
        brand_score: selectedCharity.brand_score,
      }, { withCredentials: true });
      
      const analyticsRes = await axios.get(`${BACKEND_URL}/api/analytics/overview`, { withCredentials: true });
      setCampaigns(analyticsRes.data.campaigns || []);
      setStats(analyticsRes.data);
      setSelectedCharity(null);
      setShowCreateForm(false);
      toast.success('Campaign has been added!', { description: `"${campaignForm.title}" is now live.` });
    } catch (err) {
      console.error('Error creating campaign:', err);
      const detail = err.response?.data?.detail || '';
      if (err.response?.status === 403 && detail.includes('Upgrade')) {
        if (window.confirm(`${detail}\n\nWould you like to upgrade now?`)) {
          setActiveView('billing');
        }
      } else {
        alert(detail || 'Failed to create campaign');
      }
    } finally {
      setCreating(false);
    }
  };

  const saveBudget = async () => {
    try {
      await axios.put(`${BACKEND_URL}/api/businesses/${businessProfile.business_id}`, 
        { budget }, 
        { withCredentials: true }
      );
      setShowBudgetModal(false);
    } catch (err) {
      console.error('Error saving budget:', err);
    }
  };

  const inviteUser = async () => {
    try {
      const response = await axios.post(`${BACKEND_URL}/api/auth/invite`, inviteForm, { withCredentials: true });
      setShowInviteModal(false);
      setInviteForm({ email: '', role: 'employee' });
      const usersRes = await axios.get(`${BACKEND_URL}/api/users`, { withCredentials: true });
      setUsers(usersRes.data.users || []);
      // Show success with temp password
      alert(`User invited successfully!\n\nEmail: ${response.data.message}\nTemporary Password: ${response.data.temp_password}\n\nPlease share these credentials securely with the user.`);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to invite user');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Navigation - grouped for clarity
  const navGroups = [
    { id: 'dashboard', Icon: BarChart3, label: 'Dashboard' },
    { 
      label: 'Campaigns', Icon: Target, 
      children: [
        { id: 'discover', Icon: Compass, label: 'Discover Charities' },
        { id: 'campaigns', Icon: Target, label: 'My Campaigns', badge: campaigns.length },
        { id: 'donations-mgmt', Icon: Receipt, label: 'Donations' },
        { id: 'charity-notes', Icon: MessageSquareQuote, label: 'Thank-Yous' },
        { id: 'matching', Icon: Gift, label: 'Matching' },
        { id: 'payroll', Icon: Banknote, label: 'Payroll Giving' },
        { id: 'budget', Icon: Wallet, label: 'Budget' },
      ]
    },
    { 
      label: 'Team', Icon: Users,
      children: [
        { id: 'team', Icon: Users, label: 'Members', badge: users.length },
        { id: 'volunteer', Icon: Clock, label: 'Volunteer' },
        { id: 'gamification', Icon: Trophy, label: 'Rewards' },
        { id: 'hr', Icon: Link2, label: 'HR Integrations' },
      ]
    },
    { 
      label: 'Analytics', Icon: FileText,
      children: [
        { id: 'reports', Icon: FileText, label: 'Reports' },
        { id: 'marketing', Icon: Megaphone, label: 'Marketing' },
      ]
    },
    { id: 'impact', Icon: Globe, label: 'Site Builder' },
    { 
      label: 'Settings', Icon: Settings,
      children: [
        { id: 'settings-company', Icon: Building2, label: 'Company Info' },
        { id: 'settings-branding', Icon: Palette, label: 'Branding & CSR' },
        { id: 'settings-portal', Icon: Users, label: 'Impact Portal' },
        { id: 'settings-domain', Icon: Globe, label: 'White Label' },
      ]
    },
  ];

  // Find which group the current activeView belongs to
  const activeGroup = navGroups.find(g => g.children?.some(c => c.id === activeView));

  // Check if any child in a group is active
  const isGroupActive = (group) => {
    if (group.children) return group.children.some(c => c.id === activeView);
    return group.id === activeView;
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ background: COLORS.background }}>
      {/* Top Header Navigation */}
      <header className="sticky top-0 z-50 border-b shadow-sm" style={{ background: 'var(--bg-surface)', borderColor: 'var(--border-subtle)' }}>
        <div className="flex items-center justify-between px-6 py-3">
          {/* Logo - Left */}
          <div className="flex items-center gap-3 flex-shrink-0 w-48">
            <img src="/logo.png" alt="Altrunic" className="w-10 h-10 object-contain" />
            <div>
              <h1 className="font-bold text-lg" style={{ color: COLORS.primary }}>Altrunic</h1>
              <p className="text-xs text-gray-500">Impact Platform</p>
            </div>
          </div>

          {/* Center Navigation */}
          <nav className="flex items-center gap-1 px-2 py-1.5 rounded-xl" style={{ background: COLORS.light }}>
            {navGroups.map((group, gi) => (
              <button
                key={gi}
                data-testid={`nav-${group.id || group.label.toLowerCase()}`}
                onClick={() => {
                  if (group.children) {
                    setActiveView(group.children[0].id);
                  } else {
                    setActiveView(group.id);
                  }
                }}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold transition-all whitespace-nowrap ${
                  isGroupActive(group) ? 'text-white shadow-md' : 'text-gray-600 hover:bg-white/70'
                }`}
                style={isGroupActive(group) ? { background: COLORS.primary } : {}}
              >
                <group.Icon className="w-4 h-4" />
                <span>{group.label}</span>
              </button>
            ))}
          </nav>

          {/* User Info - Right */}
          <div className="flex items-center gap-3 flex-shrink-0 justify-end relative">
            <button className="p-2 rounded-lg hover:bg-gray-100 relative">
              <Bell className="w-5 h-5 text-gray-500" />
              <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">3</span>
            </button>
            <div className="w-px h-8 bg-gray-200"></div>
            
            {/* User Dropdown */}
            <div className="relative">
              <button 
                onClick={() => setShowUserDropdown(!showUserDropdown)}
                className="flex items-center gap-2 p-2 rounded-lg hover:bg-gray-100 transition-all"
              >
                <div className="w-9 h-9 rounded-full flex items-center justify-center text-white font-bold text-sm" style={{ background: COLORS.primary }}>
                  {(user?.name || user?.email || 'U')[0].toUpperCase()}
                </div>
                <div className="text-left">
                  <p className="text-sm font-medium text-gray-900">{user?.name || user?.email?.split('@')[0]}</p>
                  <p className="text-xs text-gray-500 capitalize">{user?.role?.replace('_', ' ')}</p>
                </div>
                <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${showUserDropdown ? 'rotate-180' : ''}`} />
              </button>
              
              {/* Dropdown Menu */}
              {showUserDropdown && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setShowUserDropdown(false)} />
                  <div className="absolute right-0 top-full mt-2 w-56 bg-white rounded-xl shadow-lg border z-50 py-2">
                    <div className="px-4 py-3 border-b">
                      <p className="text-sm font-semibold text-gray-900">{user?.name || 'User'}</p>
                      <p className="text-xs text-gray-500">{user?.email}</p>
                    </div>
                    
                    <div className="py-1">
                      <button 
                        onClick={() => { setActiveView('settings-company'); setShowUserDropdown(false); }}
                        className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3"
                      >
                        <User className="w-4 h-4 text-gray-400" />
                        My Profile
                      </button>
                      <button 
                        onClick={() => { setActiveView('billing'); setShowUserDropdown(false); }}
                        className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3"
                        data-testid="dropdown-billing"
                      >
                        <CreditCard className="w-4 h-4 text-gray-400" />
                        Billing & Plan
                      </button>
                      <button 
                        onClick={() => { setShowChangePasswordModal(true); setShowUserDropdown(false); }}
                        className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3"
                      >
                        <Lock className="w-4 h-4 text-gray-400" />
                        Change Password
                      </button>
                      <button 
                        onClick={() => { setActiveView('help'); setShowUserDropdown(false); }}
                        className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3"
                        data-testid="dropdown-help"
                      >
                        <HelpCircle className="w-4 h-4 text-gray-400" />
                        Help & Support
                      </button>
                    </div>
                    
                    <div className="border-t py-1">
                      <button 
                        onClick={async () => {
                          await axios.post(`${BACKEND_URL}/api/auth/logout`, {}, { withCredentials: true });
                          localStorage.removeItem('session_token');
                          navigate('/');
                        }}
                        className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-3"
                      >
                        <LogOut className="w-4 h-4" />
                        Sign Out
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Super Admin Banner */}
        {managingBusiness && user?.role === 'super_admin' && (
          <div className="bg-purple-600 text-white px-6 py-2 flex items-center justify-between">
            <span className="font-medium">Managing: <strong>{managingBusiness.name}</strong></span>
            <button onClick={() => navigate('/admin')} className="text-sm underline">
              ← Back to Admin
            </button>
          </div>
        )}
      </header>

      {/* Change Password Modal */}
      {showChangePasswordModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full mx-4">
            <div className="p-6 border-b">
              <h3 className="text-lg font-bold text-gray-900">Change Password</h3>
              <p className="text-sm text-gray-500">Enter your current password and choose a new one</p>
            </div>
            <div className="p-6 space-y-4">
              {passwordError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
                  {passwordError}
                </div>
              )}
              {passwordSuccess && (
                <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-600">
                  Password changed successfully!
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Current Password</label>
                <input
                  type="password"
                  value={passwordForm.currentPassword}
                  onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter current password"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
                <input
                  type="password"
                  value={passwordForm.newPassword}
                  onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter new password"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Confirm New Password</label>
                <input
                  type="password"
                  value={passwordForm.confirmPassword}
                  onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Confirm new password"
                />
              </div>
            </div>
            <div className="p-6 border-t flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowChangePasswordModal(false);
                  setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
                  setPasswordError('');
                  setPasswordSuccess(false);
                }}
                className="px-4 py-2 border rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  setPasswordError('');
                  if (passwordForm.newPassword !== passwordForm.confirmPassword) {
                    setPasswordError('New passwords do not match');
                    return;
                  }
                  if (passwordForm.newPassword.length < 6) {
                    setPasswordError('Password must be at least 6 characters');
                    return;
                  }
                  try {
                    await axios.post(`${BACKEND_URL}/api/auth/change-password`, {
                      current_password: passwordForm.currentPassword,
                      new_password: passwordForm.newPassword
                    }, { withCredentials: true });
                    setPasswordSuccess(true);
                    setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
                    setTimeout(() => {
                      setShowChangePasswordModal(false);
                      setPasswordSuccess(false);
                    }, 2000);
                  } catch (err) {
                    setPasswordError(err.response?.data?.detail || 'Failed to change password');
                  }
                }}
                className="px-4 py-2 text-white rounded-lg"
                style={{ background: COLORS.primary }}
              >
                Change Password
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Content Area with Left Sidebar */}
      <main className="flex-1 flex overflow-hidden">
        {/* Left Sidebar - shows when active group has children */}
        {activeGroup && (
          <aside className="w-56 border-r bg-white flex-shrink-0 overflow-y-auto" data-testid="left-sidebar">
            <div className="p-4">
              <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
                {activeGroup.label}
              </h3>
              <nav className="space-y-1">
                {activeGroup.children.map(child => (
                  <button
                    key={child.id}
                    data-testid={`sidebar-${child.id}`}
                    onClick={() => setActiveView(child.id)}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                      activeView === child.id
                        ? 'text-white shadow-sm'
                        : 'text-gray-600 hover:bg-gray-50'
                    }`}
                    style={activeView === child.id ? { background: COLORS.primary } : {}}
                  >
                    <child.Icon className="w-4 h-4" />
                    <span>{child.label}</span>
                    {child.badge !== undefined && child.badge > 0 && (
                      <span className={`ml-auto px-1.5 py-0.5 text-xs rounded-full ${
                        activeView === child.id ? 'bg-white/20 text-white' : 'bg-gray-200 text-gray-600'
                      }`}>{child.badge}</span>
                    )}
                  </button>
                ))}
              </nav>
            </div>
          </aside>
        )}

        {/* Content Area */}
        <div className="flex-1 overflow-auto">
        {activeView.startsWith('settings-') && (
          <CompanyProfileView
            activeSection={activeView.replace('settings-', '')}
            profileForm={profileForm}
            setProfileForm={setProfileForm}
            saveCompanyProfile={saveCompanyProfile}
            savingProfile={savingProfile}
            stats={stats}
            campaigns={campaigns}
            users={users}
            businessProfile={businessProfile}
          />
        )}

        {activeView === 'dashboard' && (
          <DashboardView
            user={user}
            stats={stats}
            campaigns={campaigns}
            users={users}
            budget={budget}
            budgetUsed={budgetUsed}
            budgetPercentage={budgetPercentage}
            setActiveView={setActiveView}
            setCampaignTab={setCampaignTab}
            setShowInviteModal={setShowInviteModal}
          />
        )}

        {activeView === 'campaigns' && (
          <CampaignsView
            campaigns={campaigns}
            businessProfile={businessProfile}
            openEditCampaign={openEditCampaign}
            toggleCampaignStatus={toggleCampaignStatus}
          />
        )}

        {activeView === 'budget' && (
          <BudgetView
            budget={budget}
            budgetUsed={budgetUsed}
            budgetPercentage={budgetPercentage}
            campaigns={campaigns}
            setShowBudgetModal={setShowBudgetModal}
          />
        )}

        {activeView === 'matching' && (
          <MatchingGiftView userRole={user?.role} />
        )}

        {activeView === 'discover' && (
          <CharityDiscoverView businessProfile={businessProfile} onSelectCharity={handleCharitySelect} onDonate={handleDonate} />
        )}

        {activeView === 'donations-mgmt' && (
          <DonationsView businessProfile={businessProfile} />
        )}

        {activeView === 'payroll' && (
          <PayrollGivingView />
        )}

        {activeView === 'charity-notes' && (
          <CharityNotesView />
        )}

        {activeView === 'team' && (
          <TeamView
            users={users}
            setShowInviteModal={setShowInviteModal}
            businessId={businessProfile?.business_id || user?.business_id}
            refreshUsers={async () => {
              try {
                const usersRes = await axios.get(`${BACKEND_URL}/api/users`, { withCredentials: true });
                setUsers(usersRes.data.users || []);
              } catch (err) {
                console.log('Users refresh failed');
              }
            }}
          />
        )}

        {activeView === 'impact' && (
          <ImpactWebsiteView
            businessProfile={businessProfile}
            campaigns={campaigns}
            stats={stats}
            user={user}
          />
        )}

        {activeView === 'marketing' && (
          <MarketingView />
        )}

        {activeView === 'reports' && (
          <ReportsView />
        )}

        {activeView === 'volunteer' && (
          <VolunteerView user={user} />
        )}

        {activeView === 'gamification' && (
          <GamificationView user={user} />
        )}

        {activeView === 'hr' && (
          <HRIntegrationsView user={user} businessProfile={businessProfile} />
        )}

        {activeView === 'billing' && (
          <div className="p-6 max-w-5xl mx-auto">
            <SubscriptionView />
          </div>
        )}

        {activeView === 'help' && (
          <HelpSupportView />
        )}
        </div>
      </main>

      {/* MODALS */}
      {showOnboarding && (
        <OnboardingWizard 
          isOpen={showOnboarding}
          businessId={businessProfile?.business_id || user?.business_id}
          onComplete={() => {
            fetchInitialData();
            setShowOnboarding(false);
          }}
          onClose={() => setShowOnboarding(false)}
        />
      )}
      
      <CreateCampaignModal
        showCreateForm={showCreateForm}
        selectedCharity={selectedCharity}
        setShowCreateForm={setShowCreateForm}
        setSelectedCharity={setSelectedCharity}
        campaignForm={campaignForm}
        setCampaignForm={setCampaignForm}
        handleCreateCampaign={handleCreateCampaign}
        creating={creating}
      />
      
      <EditCampaignModal
        showEditModal={showEditModal}
        setShowEditModal={setShowEditModal}
        editingCampaign={editingCampaign}
        setEditingCampaign={setEditingCampaign}
        editForm={editForm}
        setEditForm={setEditForm}
        saveCampaignEdit={saveCampaignEdit}
        savingEdit={savingEdit}
      />
      
      <BudgetSettingsModal
        showBudgetModal={showBudgetModal}
        setShowBudgetModal={setShowBudgetModal}
        budget={budget}
        setBudget={setBudget}
        saveBudget={saveBudget}
      />
      
      <InviteUserModal
        showInviteModal={showInviteModal}
        setShowInviteModal={setShowInviteModal}
        inviteForm={inviteForm}
        setInviteForm={setInviteForm}
        inviteUser={inviteUser}
      />
      <ChatbotWidget context="support" />

      {/* Donation Modal */}
      <DonationModal
        isOpen={showDonationModal}
        onClose={() => { setShowDonationModal(false); setDonationCharity(null); }}
        charity={donationCharity}
        businessId={businessProfile?.business_id}
        donorType="guest"
        matchInfo={matchingSettings}
        onSuccess={handleDonationSuccess}
      />
    </div>
  );
};

export default UnifiedDashboard;
